import SearchIcon from '@mui/icons-material/Search';
import { SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { uniqueId } from 'lodash';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActionButtons from './ActionButtons';
import { DocumentSearchText } from '@models/searchable';
import { removeDiacritics } from '@utils/string';

const iconSx: SxProps = {
    color: 'grey.400',
    mr: 1,
};

const searchBarSx: SxProps = {
    '& input': { height: 20, typography: 'body2' },
    bgcolor: 'common.white',
    boxShadow: 1,
    pr: 0,
    width: '100%',
};

const searchContainerSx: SxProps = {
    mb: 1,
    position: 'sticky',
    top: 20,
    width: '100%',
};

export const MATCH_CLASS = 'search_match';
export const FOCUSED_CLASS = 'focused';

/**
 * Escape special characters for safe RegEx use.
 * @param  inputString The original string searched
 * @returns  The escaped value of the string for a following RegExp
 */
const regEscape = (inputString: string) => inputString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

interface SearchableText {
    originalRanges: { end: number; start: number }[];
    value: string;
}

/**
 * Fold case and diacritics for matching while retaining the corresponding ranges in the original
 * text. Combining marks are grouped with their base character so highlighted text stays intact.
 */
const getSearchableText = (text: string): SearchableText => {
    const originalRanges: SearchableText['originalRanges'] = [];
    let value = '';
    let originalIndex = 0;

    for (const match of text.matchAll(/\P{M}\p{M}*|\p{M}+/gu)) {
        const originalText = match[0];
        const normalizedText = removeDiacritics(originalText).toLowerCase();
        const start = originalIndex;
        const end = start + originalText.length;

        value += normalizedText;
        originalRanges.push(
            ...Array.from({ length: normalizedText.length }, () => ({ end, start })),
        );
        originalIndex = end;
    }

    return { originalRanges, value };
};

/**
 * This function highlights the matching text using JSX components.
 * @param  text The text in which we are looking for the searched value
 * @param  searchedTexts The texts we are highlighting
 * @returns An array of strings and JSX elements, with the matching words wrapped in a <strong> element
 */
const highlightText = (text: string, searchedTexts: string[]): (string | JSX.Element)[] => {
    const escapedSearchedTexts = searchedTexts
        .map((searchedText) => removeDiacritics(searchedText).toLowerCase())
        .filter(Boolean)
        .map(regEscape)
        .sort((a, b) => b.length - a.length);
    if (!escapedSearchedTexts.length) return [text];

    const { originalRanges, value } = getSearchableText(text);
    const highlightedText: (string | JSX.Element)[] = [];
    let lastOriginalIndex = 0;

    for (const match of value.matchAll(new RegExp(escapedSearchedTexts.join('|'), 'g'))) {
        if (match.index === undefined) continue;

        const start = originalRanges[match.index]?.start;
        const end = originalRanges[match.index + match[0].length - 1]?.end;
        if (start === undefined || end === undefined || start < lastOriginalIndex) continue;

        highlightedText.push(
            text.slice(lastOriginalIndex, start),
            <strong key={uniqueId('highlighted-')} className={MATCH_CLASS}>
                {text.slice(start, end)}
            </strong>,
        );
        lastOriginalIndex = end;
    }
    highlightedText.push(text.slice(lastOriginalIndex));

    return highlightedText;
};

/**
 * Function to correctly change node children. This version safely highlights matches without injecting raw HTML.
 * @param  initialElement The initial element as a string
 * @param  values The texts we are looking for in the element
 * @returns The new array of children for our element
 */
const buildChildrenFromString = (
    initialElement: string,
    values: string[],
): [string | (JSX.Element | string)[], number] => {
    if (!initialElement) return [initialElement, 0];

    const highlightedChildren = highlightText(initialElement, values);
    const matchCount = highlightedChildren.filter((child) => typeof child !== 'string').length;

    return [highlightedChildren, matchCount];
};

type HighlightResult = [DocumentSearchText[], number];

/**
 * A function to recursively edit the given value and add the highlight where it is needed.
 * @param  element The element where we want to add a highlight
 * @param  values The values we are looking for in the element
 * @param  key A key to keep track of element position in the tree
 * @returns The edited element with highlights
 */
const highlightValueInElement = (
    element: DocumentSearchText,
    values: string[],
    key?: string,
): [DocumentSearchText, number] | null => {
    if (!element) return null;
    if (typeof element === 'string') {
        return buildChildrenFromString(element, values);
    }

    if (Array.isArray(element)) {
        const [newElements, totalMatches] = (
            element as DocumentSearchText[]
        ).reduce<HighlightResult>(
            ([elements, total], subElement: DocumentSearchText, index: number) => {
                const result = highlightValueInElement(
                    subElement,
                    values,
                    `${key ? `${key}-` : ''}${index}`,
                );
                if (result) {
                    const [newElem, matches] = result;
                    elements.push(newElem);

                    return [elements, total + matches];
                }

                return [elements, total];
            },
            [[], 0],
        );

        return [newElements as DocumentSearchText, totalMatches];
    }

    const {
        props: { children },
    } = element;

    if (!children) {
        // No children to highlight (like a note), return as is
        return [element, 0];
    }

    if (typeof children === 'string') {
        const [newChildren, matchCount] = buildChildrenFromString(children, values);

        return [
            {
                ...element,
                props: {
                    ...element.props,
                    children: newChildren,
                },
            },
            matchCount,
        ];
    }

    if (Array.isArray(children)) {
        const [newChildren, totalMatches] = children.reduce(
            ([elements, total], child) => {
                const result = highlightValueInElement(child, values);
                if (result) {
                    const [newChild, matches] = result;
                    elements.push(newChild);

                    return [elements, total + matches];
                }
                elements.push(child);

                return [elements, total];
            },
            [[], 0],
        );

        return [
            {
                ...element,
                props: {
                    ...element.props,
                    children: newChildren,
                },
            },
            totalMatches,
        ];
    }

    return highlightValueInElement(children, values);
};

export const getUrlSearch = (search?: string | string[]): string => {
    if (typeof search === 'string') return search;
    if (Array.isArray(search)) return search[0] || '';

    return typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('search') || ''
        : '';
};

const getFallbackSearchTerms = (searchValue: string): string[] => {
    const uniqueTerms = new Map<string, string>();

    searchValue
        .trim()
        .split(/\s+/)
        .map((term) => term.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
        .filter(
            (term) =>
                term.length >= 3 ||
                (term.length >= 2 && (term === term.toLocaleUpperCase() || /^\d+$/.test(term))),
        )
        .forEach((term) => {
            const normalizedTerm = term.toLocaleLowerCase();
            if (!uniqueTerms.has(normalizedTerm)) uniqueTerms.set(normalizedTerm, term);
        });

    return [...uniqueTerms.values()];
};

interface DocumentSearchProps {
    controls?: ReactNode;
    targetId?: string;
    text: DocumentSearchText;
    updateContent: (content: DocumentSearchText) => void;
}

const DocumentSearch: FC<DocumentSearchProps> = ({ controls, targetId, text, updateContent }) => {
    const inputRef = useRef<HTMLInputElement>();
    const { t } = useTranslation('search');
    const {
        query: { search },
    } = useRouter();
    const urlSearch = getUrlSearch(search);
    const [searchValue, setSearchValue] = useState<string>(urlSearch);
    const [resultCount, setResultCount] = useState<number>(0);
    const [resultRevision, setResultRevision] = useState(0);
    const isUrlSearchRef = useRef(Boolean(urlSearch));
    const previousTargetIdRef = useRef<string>();
    const previousTextRef = useRef<DocumentSearchText>();
    const previousUpdateContentRef = useRef<(content: DocumentSearchText) => void>();
    const previousUrlSearchRef = useRef<string>();

    const onSearchChange = useCallback(
        (newValue: string, fallbackToWords = false) => {
            if (newValue) {
                let result = highlightValueInElement(text, [newValue]);
                if (!result) {
                    updateContent(text);
                    setResultCount(0);
                    setSearchValue(newValue);
                    setResultRevision((revision) => revision + 1);

                    return;
                }

                if (fallbackToWords && result[1] === 0) {
                    const fallbackTerms = getFallbackSearchTerms(newValue);
                    const fallbackResult = fallbackTerms.length
                        ? highlightValueInElement(text, fallbackTerms)
                        : null;
                    if (fallbackResult) result = fallbackResult;
                }

                const [highlightedContent, matches] = result;

                updateContent(highlightedContent);
                setResultCount(matches);
                setSearchValue(newValue);
            } else {
                updateContent(text);
                setResultCount(0);
                setSearchValue(newValue);
            }
            setResultRevision((revision) => revision + 1);
        },
        [text, updateContent],
    );

    const setInputValue = useCallback((inputValue: string) => {
        // replace multiple spaces with only one for search
        const inputElement = (inputRef.current as HTMLDivElement).querySelector('input');
        if (inputElement) {
            inputElement.value = inputValue;
        }
    }, []);

    const onSearchChangeDebounced = useMemo(
        () =>
            debounce((newValue: string) => {
                isUrlSearchRef.current = false;
                onSearchChange(newValue);
            }, 500),
        [onSearchChange],
    );

    useEffect(
        () => () => {
            onSearchChangeDebounced.cancel();
        },
        [onSearchChangeDebounced],
    );

    useEffect(() => {
        const urlChanged = previousUrlSearchRef.current !== urlSearch;
        const textChanged = previousTextRef.current !== text;
        const targetChanged = previousTargetIdRef.current !== targetId;

        if (targetChanged && previousTextRef.current && previousUpdateContentRef.current) {
            previousUpdateContentRef.current(previousTextRef.current);
        }

        previousUrlSearchRef.current = urlSearch;
        previousTargetIdRef.current = targetId;
        previousTextRef.current = text;
        previousUpdateContentRef.current = updateContent;

        if (urlChanged) {
            isUrlSearchRef.current = Boolean(urlSearch);
            setInputValue(urlSearch);
            onSearchChange(urlSearch, Boolean(urlSearch));

            return;
        }

        if (textChanged || targetChanged) {
            setInputValue(searchValue);
            onSearchChange(searchValue, isUrlSearchRef.current);
        }
        // Reapply only when the URL query or source document changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, text, urlSearch]);

    return (
        <Box sx={searchContainerSx}>
            {controls && <Box sx={{ mb: 1 }}>{controls}</Box>}
            <InputBase
                // ref is faster than setting 'value' props, for reset and search
                ref={inputRef}
                endAdornment={
                    <ActionButtons
                        inputField={inputRef.current?.querySelector('input') ?? undefined}
                        onReset={() => {
                            onSearchChange('');
                            setInputValue('');
                        }}
                        resultCount={resultCount}
                        resultRevision={resultRevision}
                        searchValue={searchValue}
                    />
                }
                onChange={({ target }) =>
                    onSearchChangeDebounced(target.value.replace(/(\s){2,}/g, '$1'))
                }
                placeholder={t('searchInDocument')}
                startAdornment={<SearchIcon sx={iconSx} />}
                sx={searchBarSx}
                type="search"
            />
        </Box>
    );
};

export default DocumentSearch;
